import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {authStorage, getErrorMessage, learningAgentApi, practiceApi, resourcesApi} from '../services/api';
import MathText from '../components/MathText';
import './InteractiveLearningAgent.css';

const list = (value) => Array.isArray(value) ? value : value?.results || [];
const uniqueId = (prefix) => `${prefix}-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
const accountKey = () => String(authStorage.get()?.user?.id || 'current');
const pendingKey = (conversationId) => `examsite.interactive.pending.${accountKey()}.${conversationId}`;
const answerKey = (attemptId) => `examsite.practice.pending.${accountKey()}.${attemptId}`;
const readSaved = (key) => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; } };
const labels = {
    ask: 'انتخاب مفهوم', example: 'مثال از منبع', explain: 'توضیح از منبع',
    recommend_resource: 'منابع پیشنهادی', assess: 'سنجش کوتاه', hint: 'راهنمای تمرین',
    review: 'مرور پیش‌نیاز', advance: 'گام بعدی', remediate: 'برنامهٔ جبرانی', no_action: 'بدون اقدام',
};
const reasonLabels = {
    no_matching_source_example: 'مثال مناسبی در منبع پیدا نشد.',
    no_visible_matching_resources: 'منبع قابل‌دسترسی دیگری پیدا نشد.',
    no_verified_prerequisite_gap: 'نیاز به مرور پیش‌نیاز تأیید نشده است.',
    assessment_unavailable: 'سنجش کوتاه فعلاً در دسترس نیست.',
    remediation_cooldown: 'برنامهٔ جبرانی تازه‌ای فعلاً قابل شروع نیست.',
    persistent_gap_not_established: 'برای برنامهٔ جبرانی هنوز شواهد کافی وجود ندارد.',
    NO_APPROVED_ITEM: 'سؤال تأییدشدهٔ مناسب پیدا نشد.',
};
const messageFor = (codes) => (codes || []).map((code) => reasonLabels[code] || code).join('، ');

function PracticeCard({turn, conversationId, onOutcome, onHint, onAbandon}) {
    const attemptId = turn.result?.attempt_id;
    const [item, setItem] = useState(null);
    const [status, setStatus] = useState(turn.status);
    const [answer, setAnswer] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [pending, setPending] = useState(() => readSaved(answerKey(attemptId)));
    useEffect(() => {
        if (!attemptId) return undefined;
        let live = true;
        const refresh = async () => {
            try {
                const current = await practiceApi.status(attemptId);
                if (!live) return;
                setStatus(current.status);
                if (current.status === 'ISSUED') {
                    const response = await practiceApi.item(attemptId);
                    if (live) setItem(response.item);
                } else if (['GRADED', 'NEEDS_REVIEW'].includes(current.status)) {
                    sessionStorage.removeItem(answerKey(attemptId));
                    setPending(null);
                    if (current.status === 'GRADED') onOutcome(turn.turn_id);
                }
            } catch (requestError) { if (live) setError(getErrorMessage(requestError)); }
        };
        refresh();
        return () => { live = false; };
    }, [attemptId, onOutcome, turn.turn_id]);

    const submit = async (event) => {
        event.preventDefault();
        if (!item || busy || (!answer.trim() && !pending)) return;
        const saved = pending || {
            event_id: uniqueId('answer'),
            submitted_answer: item.question_type === 'mcq' ? {option_id: answer} : {text: answer.trim()},
        };
        sessionStorage.setItem(answerKey(attemptId), JSON.stringify(saved));
        setPending(saved); setBusy(true); setError('');
        try {
            const result = await practiceApi.submit(attemptId, saved);
            sessionStorage.removeItem(answerKey(attemptId)); setPending(null);
            setStatus(result.status);
            if (result.status === 'GRADED') onOutcome(turn.turn_id);
        } catch (requestError) {
            try {
                const current = await practiceApi.status(attemptId);
                if (['GRADED', 'NEEDS_REVIEW'].includes(current.status)) {
                    sessionStorage.removeItem(answerKey(attemptId)); setPending(null); setStatus(current.status);
                    if (current.status === 'GRADED') onOutcome(turn.turn_id);
                } else setError(getErrorMessage(requestError));
            } catch { setError(getErrorMessage(requestError)); }
        } finally { setBusy(false); }
    };

    if (!attemptId) return <p>{messageFor(turn.result?.reason_codes) || 'فعلاً سؤال مناسبی در دسترس نیست.'}</p>;
    if (status === 'NEEDS_REVIEW') return <p>پاسخت ثبت شد و منتظر بازبینی معلم است.</p>;
    if (status === 'GRADED') return <p>پاسخت ارزیابی شد. ادامهٔ یادگیری در حال آماده‌شدن است.</p>;
    if (status === 'EXPIRED') return <p>مهلت این تمرین پایان یافته است.</p>;
    return <div className="ila-practice">
        {item ? <form onSubmit={submit}>
            <p className="ila-question"><MathText text={item.stem}/></p>
            {item.question_type === 'mcq' ? <div className="ila-options">{(item.options || []).map((option) =>
                <label key={option.option_id}><input type="radio" name={`answer-${attemptId}`} value={option.option_id}
                    checked={answer === option.option_id} disabled={Boolean(pending) || busy}
                    onChange={() => setAnswer(option.option_id)}/><MathText text={option.text}/></label>)}</div>
                : <textarea aria-label="پاسخ تشریحی" value={answer} disabled={Boolean(pending) || busy}
                    onChange={(event) => setAnswer(event.target.value)} rows={4}/>}
            <div className="ila-actions">
                <button type="submit" disabled={busy || (!answer.trim() && !pending)}>{pending ? 'بررسی ثبت پاسخ' : 'ثبت پاسخ'}</button>
                {onHint && <button type="button" className="secondary" onClick={() => onHint(turn)}>راهنمایی می‌خواهم</button>}
                {onAbandon && <button type="button" className="secondary" onClick={() => onAbandon(turn)}>ترک تمرین</button>}
            </div>
        </form> : <p>در حال دریافت سؤال…</p>}
        {error && <p role="alert" className="ila-error">{error}</p>}
    </div>;
}

function TurnCard({turn, conversationId, path, onReply, onPath, onOutcome, onInteraction}) {
    const result = turn.result || {};
    const blocked = ['blocked', 'stale_source', 'expired', 'no_action'].includes(turn.status);
    const action = turn.selected_action;
    const cardRef = useRef(null);
    const [resourceError, setResourceError] = useState('');
    useEffect(() => {
        const content = ['example', 'explain'].includes(action) && turn.status === 'completed' && Boolean(result.text);
        const hint = action === 'hint' && turn.status === 'completed' && Boolean(result.text);
        if (!content && !hint) return undefined;
        let visibleSince = null;
        let engagedSeconds = 0;
        const countVisible = () => {
            if (visibleSince !== null) {
                engagedSeconds += Math.max(0, (Date.now() - visibleSince) / 1000);
                visibleSince = null;
            }
        };
        const report = () => {
            countVisible();
            const seconds = Math.min(900, Math.floor(engagedSeconds));
            if (content && seconds >= 1) onInteraction(turn, 'content_engaged', {active_seconds: seconds}, `engaged-${turn.turn_id}`);
        };
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                if (content) onInteraction(turn, 'content_viewed', {}, `view-${turn.turn_id}`);
                if (hint) onInteraction(turn, 'hint_viewed', {}, `hint-view-${turn.turn_id}`);
                if (!document.hidden && visibleSince === null) visibleSince = Date.now();
            } else { countVisible(); report(); }
        }, {threshold: 0.5});
        const visibility = () => {
            if (document.hidden) { countVisible(); report(); }
            else if (visibleSince === null && cardRef.current?.getBoundingClientRect().top < window.innerHeight) {
                visibleSince = Date.now();
            }
        };
        if (cardRef.current) observer.observe(cardRef.current);
        document.addEventListener('visibilitychange', visibility);
        return () => { report(); observer.disconnect(); document.removeEventListener('visibilitychange', visibility); };
    }, [action, onInteraction, result.text, turn]);

    const openResource = async (resource) => {
        setResourceError('');
        try {
            const {blob} = await resourcesApi.file(resource.resource_id);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer';
            document.body.appendChild(link); link.click(); link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            onInteraction(turn, 'recommendation_opened', {resource_id: String(resource.resource_id)}, `resource-${turn.turn_id}-${resource.resource_id}`);
        } catch (requestError) { setResourceError(getErrorMessage(requestError)); }
    };

    const details = messageFor(result.reason_codes || result.warnings);
    return <article ref={cardRef} className={`ila-turn ${blocked ? 'ila-blocked' : ''}`} aria-label={labels[action] || 'پاسخ دستیار'}>
        <div className="ila-turn-head"><strong>{labels[action] || 'پاسخ دستیار'}</strong><span>{turn.status === 'awaiting_review' ? 'در انتظار بازبینی' : blocked ? 'نیازمند بررسی' : turn.status === 'awaiting_learner' ? 'منتظر انتخاب شما' : 'آماده'}</span></div>
        {blocked ? <p>{turn.status === 'stale_source' ? 'منبع این پاسخ تغییر کرده است؛ باید دوباره بررسی شود.' : details || 'این اقدام فعلاً ممکن نیست.'}</p> : <>
            {action === 'ask' && <><p><MathText text={result.question}/></p><div className="ila-actions">{(result.options || []).map((option) =>
                <button key={option.concept_id} disabled={turn.status !== 'awaiting_learner'} onClick={() => onReply(option.name, turn, option.concept_id)}>{option.name}</button>)}</div></>}
            {['example', 'explain'].includes(action) && <>
                {result.approved_text && <div className="ila-approved"><b>تأییدشده توسط معلم</b><p><MathText text={result.approved_text}/></p></div>}
                {result.text && <div><b>از متن منبع</b><p><MathText text={result.text}/></p></div>}
                {(result.references || []).map((ref, index) => <details key={`${ref.resource_id}-${index}`}><summary>ارجاع {index + 1} · {ref.logical_path || 'منبع'}</summary><blockquote><MathText text={ref.quote}/></blockquote></details>)}
                {result.text && <div className="ila-actions"><button className="secondary" onClick={() => onInteraction(turn, 'feedback_helpful', {}, `helpful-${turn.turn_id}`)}>مفید بود</button><button className="secondary" onClick={() => onInteraction(turn, 'feedback_not_understood', {}, `unclear-${turn.turn_id}`)}>متوجه نشدم</button></div>}
            </>}
            {action === 'recommend_resource' && ((result.recommendations || []).length ? <ul>{result.recommendations.map((resource) => <li key={resource.resource_id}>{resource.title} <button className="secondary" onClick={() => openResource(resource)}>بازکردن منبع</button></li>)}</ul> : <p>منبع قابل‌دسترسی دیگری پیدا نشد.</p>)}
            {resourceError && <p role="alert" className="ila-error">{resourceError}</p>}
            {action === 'assess' && <PracticeCard turn={turn} conversationId={conversationId} onOutcome={onOutcome}
                onHint={(source) => onReply('راهنمایی کن', source)} onAbandon={(source) => onInteraction(source, 'practice_abandoned', {}, `abandon-${source.turn_id}`)}/>}
            {action === 'hint' && <><p><MathText text={result.text}/></p><small>راهنمای سطح {result.hint_level || '—'}</small></>}
            {action === 'review' && <><p>مرور پیش‌نیاز: {result.suggested_concept_name || result.suggested_concept_id}</p>{result.suggested_concept_id && <button onClick={() => onReply(`می‌خواهم ${result.suggested_concept_name || result.suggested_concept_id} را مرور کنم`, null, result.suggested_concept_id)}>مرور این مفهوم</button>}</>}
            {action === 'advance' && <><p>گام پیشنهادی: {result.proposed_concept_name || result.proposed_concept_id}</p>{turn.status === 'awaiting_learner' && <div className="ila-actions"><button onClick={() => onPath('accept')}>پذیرش گام بعد</button><button className="secondary" onClick={() => onPath('cancel')}>فعلاً ادامه نمی‌دهم</button></div>}</>}
            {action === 'remediate' && <><p>گام {result.completed_steps || 0} از {result.max_steps || (result.steps || []).length} تکمیل شده است.</p><ol>{(result.steps || []).map((step, index) => <li key={index}>{labels[step] || step}</li>)}</ol>{turn.status === 'awaiting_learner' && <div className="ila-actions"><button onClick={() => onReply('ادامه', turn)}>ادامهٔ برنامه</button><button className="secondary" onClick={() => onReply('لغو', turn)}>لغو برنامه</button></div>}</>}
            {action === 'no_action' && <p>{details || 'فعلاً اقدامی پیشنهاد نشده است.'}</p>}
        </>}
        {details && !blocked && <small className="ila-reason">{details}</small>}
        {action === 'assess' && turn.continuation_turn_id && <small>گام بعدی به گفت‌وگو اضافه شد.</small>}
    </article>;
}

export default function InteractiveLearningAgent() {
    const {conversationId} = useParams();
    const navigate = useNavigate();
    const [mode, setMode] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [graphs, setGraphs] = useState([]);
    const [active, setActive] = useState(null);
    const [turns, setTurns] = useState([]);
    const [path, setPath] = useState(null);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const activeId = useRef(null);
    const polling = useRef(null);

    const reload = useCallback(async (id) => {
        if (!id) return;
        const [conversation, turnData, pathData] = await Promise.all([
            learningAgentApi.conversation(id), learningAgentApi.turns(id), learningAgentApi.path(id),
        ]);
        if (activeId.current !== id) return;
        setActive(conversation);
        setTurns([...(turnData.turns || [])].sort((a, b) => a.revision - b.revision));
        setPath(pathData.path);
        return {conversation, turns: turnData.turns || []};
    }, []);

    useEffect(() => {
        let live = true;
        setLoading(true);
        Promise.all([learningAgentApi.mode(), learningAgentApi.conversations()]).then(async ([modeResult, data]) => {
            if (!live) return;
            setMode(modeResult);
            const items = list(data);
            if (!items.length && modeResult.interactive_teacher_enabled) {
                // An unbound conversation can list visible graphs before a graph is selected.
                items.push(await learningAgentApi.createConversation({title: 'انتخاب درس'}));
            }
            setConversations(items);
            const selected = items.find((item) => String(item.id) === conversationId) || items.find((item) => item.graph_id) || items[0];
            if (selected) {
                activeId.current = selected.id;
                await reload(selected.id);
            }
            if (selected) {
                const graphResult = await learningAgentApi.invokeTool(selected.id, 'list_my_learning_graphs', {});
                if (live) setGraphs(graphResult.graphs || []);
            }
        }).catch((requestError) => { if (live) setError(getErrorMessage(requestError)); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [conversationId, reload]);

    useEffect(() => {
        if (!active?.id) return undefined;
        const saved = readSaved(pendingKey(active.id));
        if (saved) setText(saved.message || '');
        return undefined;
    }, [active?.id]);

    const chooseGraph = async (event) => {
        const id = Number(event.target.value);
        if (!id) return;
        setBusy(true); setError('');
        try {
            let item = conversations.find((row) => row.graph_id === id);
            if (!item) {
                const graph = graphs.find((row) => row.graph_id === id);
                item = await learningAgentApi.createConversation({graph_id: id, title: graph?.resource_name || 'گفت‌وگوی یادگیری'});
                setConversations((current) => [item, ...current]);
            }
            navigate(`/learning-agent/${item.id}`);
        } catch (requestError) { setError(getErrorMessage(requestError)); }
        finally { setBusy(false); }
    };

    const send = async (message, replyTo = null, conceptId = null) => {
        if (!active?.id || !message.trim() || busy || !mode?.interactive_teacher_enabled) return;
        const id = active.id;
        const key = pendingKey(id);
        const saved = readSaved(key);
        const payload = saved && saved.message === message.trim() && saved.reply_to_turn_id === (replyTo?.turn_id || undefined)
            ? saved : {
                client_message_id: uniqueId('message'), expected_revision: active.revision,
                message: message.trim(), ...(replyTo ? {reply_to_turn_id: replyTo.turn_id} : {}),
                ...(conceptId ? {target_concept_id: conceptId} : {}),
            };
        sessionStorage.setItem(key, JSON.stringify(payload));
        setBusy(true); setError(''); setNotice('');
        try {
            await learningAgentApi.sendInteractiveMessage(id, payload);
            sessionStorage.removeItem(key);
            if (activeId.current === id) { setText(''); await reload(id); }
        } catch (requestError) {
            if (requestError.status === 409) {
                const latestState = await reload(id).catch(() => null);
                const recorded = latestState?.turns?.some((turn) => turn.revision > payload.expected_revision
                    && turn.message === payload.message && (turn.reply_to_turn_id || null) === (payload.reply_to_turn_id || null));
                sessionStorage.removeItem(key);
                if (recorded) { setText(''); setNotice('پیام شما قبلاً ثبت شده بود.'); }
                else setError('جلسه به‌روز شده است. متن شما حفظ شد؛ دوباره ارسال کنید.');
            } else setError(getErrorMessage(requestError));
        } finally { setBusy(false); }
    };

    const interaction = useCallback(async (turn, eventType, extra = {}, eventId = null) => {
        if (!mode?.interactive_teacher_enabled || !active?.id) return;
        const key = `examsite.interaction.${accountKey()}.${active.id}.${eventId || `${turn.turn_id}-${eventType}`}`;
        if (sessionStorage.getItem(key) === 'done') return;
        const payload = {client_event_id: eventId || uniqueId('event'), turn_id: turn.turn_id, event_type: eventType, ...extra};
        try {
            await learningAgentApi.interaction(active.id, payload);
            sessionStorage.setItem(key, 'done');
            if (eventType.startsWith('feedback_')) setNotice('بازخوردت ثبت شد.');
        } catch { /* Interaction delivery never blocks learning. */ }
    }, [active?.id, mode?.interactive_teacher_enabled]);

    const refreshOutcome = useCallback((turnId) => {
        if (!activeId.current || polling.current) return;
        let attempts = 0;
        const id = activeId.current;
        const poll = async () => {
            if (document.hidden || activeId.current !== id) { polling.current = null; return; }
            try {
                const turn = await learningAgentApi.turn(id, turnId);
                if (turn.continuation_turn_id) { await reload(id); polling.current = null; return; }
            } catch { /* Continue with a bounded retry. */ }
            attempts += 1;
            if (attempts >= 12) { polling.current = null; setNotice('پاسخ ثبت شده است. ادامهٔ یادگیری را کمی بعد با تازه‌سازی ببینید.'); return; }
            polling.current = setTimeout(poll, Math.min(10000, 2000 * attempts));
        };
        poll();
    }, [reload]);
    useEffect(() => () => { if (polling.current) clearTimeout(polling.current); polling.current = null; }, [active?.id]);

    const changePath = async (kind) => {
        if (!active?.id || !path?.revision || busy) return;
        setBusy(true); setError('');
        try {
            if (kind === 'accept') await learningAgentApi.pathAccept(active.id, path.revision);
            else await learningAgentApi.pathCancel(active.id, path.revision);
            await reload(active.id);
        } catch (requestError) { await reload(active.id).catch(() => {}); setError(getErrorMessage(requestError)); }
        finally { setBusy(false); }
    };

    const currentGraph = graphs.find((graph) => graph.graph_id === active?.graph_id);
    const latest = useMemo(() => turns.at(-1), [turns]);
    return <main className="ila-page" dir="rtl">
        <header className="ila-header"><div><small>دستیار یادگیری</small><h1>{currentGraph?.resource_name || 'جلسهٔ یادگیری'}</h1></div><button className="secondary" onClick={() => reload(active?.id).catch((e) => setError(getErrorMessage(e)))} disabled={!active?.id}>تازه‌سازی</button></header>
        {loading && <p>در حال بارگذاری جلسه…</p>}
        {error && <div className="ila-banner ila-error" role="alert">{error}</div>}
        {notice && <div className="ila-banner" role="status">{notice}</div>}
        {mode && !mode.interactive_teacher_enabled && <div className="ila-banner">گفت‌وگوی تعاملی فعلاً فعال نیست. سابقهٔ جلسه قابل مشاهده است.</div>}
        <div className="ila-toolbar"><label>درس <select value={active?.graph_id || ''} onChange={chooseGraph} disabled={busy || !graphs.length}><option value="">انتخاب درس</option>{graphs.map((graph) => <option key={graph.graph_id} value={graph.graph_id}>{graph.resource_name}</option>)}</select></label>
            <label>گفت‌وگو <select value={active?.id || ''} onChange={(event) => navigate(`/learning-agent/${event.target.value}`)} disabled={!conversations.length}><option value="">انتخاب گفت‌وگو</option>{conversations.map((row) => <option key={row.id} value={row.id}>{row.title || 'گفت‌وگو'}</option>)}</select></label>
            <button className="secondary" disabled={!active?.graph_id || busy} onClick={async () => { try { const row = await learningAgentApi.createConversation({graph_id: active.graph_id, title: 'گفت‌وگوی جدید'}); setConversations((current) => [row, ...current]); navigate(`/learning-agent/${row.id}`); } catch (e) { setError(getErrorMessage(e)); } }}>گفت‌وگوی جدید</button></div>
        <div className="ila-layout"><section className="ila-chat" aria-label="گفت‌وگو">
            {!active?.graph_id && <p className="ila-empty">برای شروع، یک درس را انتخاب کنید.</p>}
            {active?.graph_id && !turns.length && <p className="ila-empty">دربارهٔ این درس سؤال بپرسید یا یک مثال بخواهید.</p>}
            {turns.map((turn) => <div key={turn.turn_id} className="ila-exchange"><div className="ila-user-message"><MathText text={turn.message}/></div><TurnCard turn={turn} conversationId={active.id} path={path}
                onReply={send} onPath={changePath} onOutcome={refreshOutcome} onInteraction={interaction}/></div>)}
            {latest?.selected_action === 'assess' && latest.status === 'graded' && !latest.continuation_turn_id && <p>ادامهٔ یادگیری در حال آماده‌شدن است.</p>}
            <form className="ila-composer" onSubmit={(event) => { event.preventDefault(); send(text); }}><textarea aria-label="پیام به دستیار" value={text} onChange={(event) => setText(event.target.value)} disabled={!active?.graph_id || !mode?.interactive_teacher_enabled || busy} placeholder="سؤالت را بنویس…"/><button type="submit" disabled={!text.trim() || busy || !mode?.interactive_teacher_enabled}>ارسال</button></form>
        </section><aside className="ila-side"><h2>مسیر یادگیری</h2>{path?.steps?.length ? <ol>{path.steps.map((step, index) => <li key={index} className={index === path.cursor ? 'current' : ''}>{step.concept_name || step.name || step.concept_id || String(step)}</li>)}</ol> : <p>پس از شروع یادگیری، مسیر اینجا نمایش داده می‌شود.</p>}</aside></div>
    </main>;
}

export function PracticePage() {
    const {attemptId} = useParams();
    const navigate = useNavigate();
    const turn = useMemo(() => ({turn_id: attemptId, status: 'issued', result: {attempt_id: attemptId}}), [attemptId]);
    return <main className="ila-page" dir="rtl"><header className="ila-header"><h1>سنجش کوتاه</h1><button className="secondary" onClick={() => navigate('/learning-agent')}>بازگشت به یادگیری</button></header>
        <section className="ila-chat"><PracticeCard turn={turn} onOutcome={() => {}}/></section></main>;
}
